import { useCallback } from 'react';
import { useEventFeedStore } from '@/stores/event-feed.store';
import type { Event } from '@/types/event';

interface WatchingState {
    isWatching: boolean;
}

const TIME_MATCH_BUFFER_SECONDS = 0.5;

interface WatchingStateOptions {
    isFocused?: boolean;
}

type GetWatchingState = (
    event: Event,
    options?: WatchingStateOptions,
) => WatchingState;

export function useWatchingState(): GetWatchingState {
    const playerCurrentTime = useEventFeedStore(
        (state) => state.playerCurrentTime,
    );
    const playerLoopEnabled = useEventFeedStore(
        (state) => state.playerLoopEnabled,
    );
    const playerHasSeeked = useEventFeedStore((state) => state.playerHasSeeked);
    const playingEventId = useEventFeedStore(
        (state) => state.feedPlayingEventId,
    );

    return useCallback(
        (event, options) => {
            const isFocused = options?.isFocused ?? playingEventId === event.id;
            const shouldCheckEventTime =
                playerHasSeeked || playerLoopEnabled === false;
            const isWithinEventTime =
                !shouldCheckEventTime ||
                isPlayheadWithinEventTime(event, playerCurrentTime);
            return {
                isWatching: isFocused && isWithinEventTime,
            };
        },
        [playerCurrentTime, playerLoopEnabled, playerHasSeeked, playingEventId],
    );
}

function isPlayheadWithinEventTime(
    event: Event,
    currentTime: number | null,
): boolean {
    if (currentTime === null || event.frames.length === 0) return false;

    const firstFrame = event.frames[0];
    const lastFrame = event.frames[event.frames.length - 1];
    const startTime = firstFrame.time;
    const endTime = lastFrame.time;

    return (
        currentTime >= startTime - TIME_MATCH_BUFFER_SECONDS &&
        currentTime <= endTime + TIME_MATCH_BUFFER_SECONDS
    );
}
